import { useLayoutEffect } from "react";

// 🚀 Pages publiques préparées à la construction (tour 55). Après « craco build »,
// scripts/prerendu.js ouvre chaque page publique dans un navigateur simulé (jsdom), sans réseau, et
// garde son HTML au moment où la page apparaît. nginx sert ce HTML : la page s'affiche avant que le
// programme ne soit téléchargé. Le programme dessine ensuite la même page dans #root, caché sous le
// HTML préparé (#prerendu), et prend le relais quand sa page apparaît : le HTML préparé et ses styles
// sont retirés avant que le navigateur n'affiche l'image suivante. Rien ne bouge à l'écran.
export const PRERENDER_ID = "prerendu";
// Délai de secours : page jamais apparue (adresse inattendue, erreur), le programme s'affiche quand même
export const RELAY_FALLBACK_MS = 5000;

// 👤 Fiche servie par l'API (tour 59) : page introuvable (404) pour l'adresse ouverte, d'après le repère
// <meta name="acadeclic-statut" content="404"> de la page servie. Lu une fois au chargement puis retiré :
// il ne vaut que pour cette adresse (pas pour la fiche suivante ouverte dans le site)
const SERVED = (() => {
  if (typeof document === "undefined" || typeof window === "undefined") return null;
  const meta = document.querySelector('meta[name="acadeclic-statut"]');
  if (!meta) return null;
  const status = meta.getAttribute("content");
  meta.remove();
  return { status, href: `${window.location.pathname}${window.location.search}` };
})();
export const servedNotFound = ({ pathname, search } = {}) => SERVED?.status === "404" && SERVED.href === `${pathname}${search || ""}`;

export const hasPrerender = () => typeof document !== "undefined" && !!document.getElementById(PRERENDER_ID);
// Page ouverte avec un HTML préparé : lu au chargement du programme, avant tout relais (le relais peut
// avoir lieu avant les effets des autres composants de la même image)
const OPENED_PREPARED = hasPrerender();
export const openedPrepared = () => OPENED_PREPARED;
// 👤 Fiche servie par l'API avec son haut déjà écrit (tour 59, A2) : le relais attend que la fiche du programme
// soit prête (annonce et interrupteurs arrivés), pas sa page d'attente en barres grises. ⏸️ Fiche en pause (tour
// 60, A1, repère « pause ») : la fiche demande l'annonce (404 « advert_paused ») puis affiche la même page
const OPENED_FICHE_SERVED = typeof document !== "undefined" && (!!document.querySelector(`#${PRERENDER_ID} .ndf--servie`) || SERVED?.status === "pause");
export const preparedFicheServed = () => OPENED_FICHE_SERVED;

// Animations de la page du site (tracé des courbes, démonstration de l'accueil…) reprises au point où en
// sont celles de la page préparée, élément pour élément (même animation, même classe). Sans cela, elles
// recommenceraient au relais : une courbe déjà tracée s'effacerait puis se retracerait
export const syncAnimations = (from, to) => {
  if (!from?.getAnimations || !to?.getAnimations) return;
  const keyOf = (animation) => `${animation.animationName || ""}|${animation.effect?.target?.getAttribute?.("class") || ""}`;
  const times = new Map();
  for (const animation of from.getAnimations({ subtree: true })) if (!times.has(keyOf(animation))) times.set(keyOf(animation), animation.currentTime);
  if (!times.size) return;
  for (const animation of to.getAnimations({ subtree: true })) {
    const time = times.get(keyOf(animation));
    if (time !== undefined && time !== null) animation.currentTime = time;
  }
};

// Relais : pendant la construction, prévient scripts/prerendu.js (la page est prête à être gardée) ;
// dans le navigateur, retire le HTML préparé et ses styles. Sans HTML préparé : rien.
export const relayPrerender = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const building = window.__PRERENDU__;
  if (building && typeof building.pret === "function") {
    building.pret();
    return;
  }
  const prepared = document.getElementById(PRERENDER_ID);
  if (!prepared) return;
  syncAnimations(prepared, document.getElementById("root"));
  prepared.remove();
  document.querySelectorAll("style[data-prerendu]").forEach((style) => style.remove());
};

// Page publique préparée : relais dès que la page est dessinée, avant son affichage. ready : faux tant
// que la page n'affiche pas encore ce que le HTML préparé montre
export const usePrerenderRelay = (ready = true) => {
  useLayoutEffect(() => {
    if (ready) relayPrerender();
  }, [ready]);
};
